<template>
  <div>
    <Spinner id="spinner" :is-loading="isLoading" />
    <div v-if="!isLoading" id="toggles-container" class="card-item">
      <div
        v-for="(toggle, i) in toggles"
        :id="toggle.name"
        :key="toggle.name"
        class="toggle card-item shadowless">
        <div class="toggle-body">
          <b-field class="is-pulled-right">
            <b-switch
              v-model="toggle.isActive"
              type="is-success"
              size="is-small"
              :disabled="!checkAllUserFeature(toggle.name) || toggle.isDisabled"
              @input="updateFeatureToggle(i, toggle._id, $event)" />
          </b-field>
          <p class="title is-size-6 has-text-primary mb-0">{{ toggle.name }}</p>
          <p><small>{{ toggle.description }}</small></p>
        </div>
        <div class="is-clearfix toggle-footer">
          <button
            v-if="toggle.options && toggle.options.length > 0"
            :disabled="!checkAllUserFeature(toggle.name)"
            class="is-pulled-right button is-button-secondary is-rounded"
            @click="extendOption(i)">
            <span class="icon is-small">
              <i class="fas" :class="extendedOptions === i ? 'fa-chevron-up' : 'fa-chevron-down'" />
            </span>
            <span>Opciones</span>
          </button>
        </div>
        <div v-if="toggle.options && toggle.options.length > 0" class="toggle-options" :class="{ 'hidden-options': extendedOptions !== i }">
          <b-field
            v-for="option in toggle.options"
            :key="option.key"
            :label="option.description"
            custom-class="has-text-weight-normal">
            <b-numberinput
              v-if="typeof option.value === 'number'"
              :id="`option${option.key}`"
              v-model="option.value"
              min="0"
              size="is-small"
              controls-rounded
              rounded
              class="number-input"
              @input="$set(toggle, 'hasChanged', true)" />
            <b-input
              v-if="typeof option.value === 'string'"
              :id="`option${option.key}`"
              v-model="option.value"
              size="is-small"
              rounded
              placeholder="Debes ingresar un valor..."
              @input="$set(toggle, 'hasChanged', true)" />
          </b-field>
          <button
            v-if="toggle.hasChanged"
            class="is-pulled-right button is-button-primary is-rounded"
            :disabled="toggle.isDisabled"
            @click="updateFeatureToggle(i, toggle._id, toggle.isActive, toggle.options)">
            <span class="icon is-small">
              <i class="fas" :class="toggle.isDisabled ? 'fa-cog fa-spin' : 'fa-save'" />
            </span>
            <span>Guardar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from '@/ui/components/Spinner.vue';
import { getAllFeaturesToggles, updateFeatureToggle } from '@/application/services/api-clients/features-toggles';
import userFeatures from '@/ui/mixins/user-features';

export default {
  name: 'FeaturesToggles',
  components: { Spinner },
  mixins: [userFeatures],
  props: {
    featuresToShow: { type: Array, default: () => [] }
  },
  data() {
    return {
      isLoading: true,
      extendedOptions: undefined,
      toggles: []
    };
  },
  async mounted() {
    try {
      this.toggles = await getAllFeaturesToggles();
      const { allowedUserFeatures } = this.$store.state.currentUser.user;
      const generalButton = allowedUserFeatures.find(name => name === 'element.settings.feature-toggle.button.general');
      // En caso que se desee usar la vista configuración en otro sitio con un subset de featureToggles
      if (this.featuresToShow.length > 0) {
        this.toggles = this.toggles.filter(toggle => this.featuresToShow.includes(toggle.name));
      // En caso que sólo se deseen mostrar features toggles con permisos de usuario específicos y no todos
      } else if (!generalButton) {
        const userToggleFeatureNames = allowedUserFeatures
          .filter(feature => feature.includes('element.settings.feature-toggle.button'))
          .map(feature => feature.split('.').pop());
        const featuresActive = this.toggles
          .filter(toggle => userToggleFeatureNames.includes(toggle.name))
          .map(toggle => toggle.name);
        this.toggles = this.toggles.filter(toggle => featuresActive.includes(toggle.name));
      }
      this.isLoading = false;
    } catch (_error) {
      this.$buefy.toast.open({
        position: 'is-bottom-left',
        message: 'Hubo un problema al conectarnos con el servidor',
        type: 'is-danger',
        duration: 5000
      });
    }
  },
  methods: {
    checkAllUserFeature(name) {
      // update-transaction-debt-date
      const general = this.checkUserFeature('element.settings.feature-toggle.button.general');
      const featureUserToggleName = `element.settings.feature-toggle.button.${name}`;
      const specific = this.checkUserFeature(featureUserToggleName);
      return general || specific;
    },
    async updateFeatureToggle(i, featureToggleId, isActive, options) {
      this.$set(this.toggles[i], 'isDisabled', true);
      let result;
      try {
        result = await updateFeatureToggle(featureToggleId, isActive, options);
        this.$set(this.toggles, i, result);
        this.$buefy.toast.open({
          position: 'is-bottom-left',
          message: 'Información actualizada correctamente',
          type: 'is-info',
          duration: 5000
        });
      } catch (_error) {
        this.$buefy.toast.open({
          position: 'is-bottom-left',
          message: 'Hubo un problema al conectarnos con el servidor',
          type: 'is-danger',
          duration: 5000
        });
        this.$set(this.toggles[i], 'isDisabled', false);
      }
    },
    extendOption(i) {
      if (this.extendedOptions === i) {
        this.extendedOptions = undefined;
        return;
      }
      this.extendedOptions = i;
    }
  }
};
</script>

<style scoped>
  .toggle {
    margin-bottom: 1.1rem;
    display: block;
    overflow: visible;
  }
  .toggle:last-child {
    margin-bottom: 0;
  }
  .toggle-footer {
    margin-top: .5rem
  }
  .toggle-options {
    overflow: hidden;
    transition: all .3s ease-in-out;
    transform-origin: top;
    height: auto;
    opacity: 1;
    margin: 1rem;
    display: block;
  }
  .hidden-options {
    opacity: 0;
    height: 0;
    display: none;
  }
  .toggle-options .columns {
    margin-top: .5rem
  }
  .toggle-options .columns:last-child {
    margin-bottom: .1rem;
  }
  .toggle-options > button {
    margin-top: 1rem;
  }
  @media (max-width: 400px) {
    .toggle-options {
      margin: 0;
    }
  }
</style>
